import type { ComponentProps } from 'react'
import { classNames } from '~/utils/client/helpers'

export type TableCellBaseProps = {
  label?: string | React.ReactNode
  isHeadline?: boolean
  alignment?: 'left' | 'right' | 'center'
  isFixed?: boolean
  hasPadding?: boolean
  className?: string
}

export const TableCell = ({
  className,
  children,
  label = '',
  isHeadline,
  alignment = 'left',
  isFixed = false,
  hasPadding = true,
  ...props
}: TableCellBaseProps & ComponentProps<'td'>) => {
  const dynamicClassName = classNames(
    isHeadline ? 'text-black font-medium' : '',
    alignment === 'right'
      ? 'flex justify-end text-right'
      : alignment === 'center'
      ? 'flex justify-center text-center'
      : '',
    isFixed
      ? 'flex sticky top-0 bg-transparent right-0 py-0 h-full items-center justify-center'
      : '',
    hasPadding ? 'px-4 py-3' : ''
  )

  return (
    <td className={classNames(dynamicClassName, className)} {...props}>
      {children ? children : label}
    </td>
  )
}

export type TableRowBaseProps = {
  row?: TableRow
}

export type TableRowProps = {
  renderCell?: (cell: TableCellBaseProps, index: number) => React.ReactNode
} & ComponentProps<'tr'>

export const TableRow = ({
  children,
  renderCell,
  className,
  row,
  ...props
}: TableRowProps & TableRowBaseProps) => {
  return (
    <tr
      className={classNames(
        'even:bg-surface-inset odd:bg-white hover:even:bg-accent-muted hover:odd:bg-accent-muted relative',
        /* Indicator */ 'hover:before:absolute before:h-full before:w-1 before:bg-black hover:before:bg-blue-300',
        className
      )}
      {...props}
    >
      {children
        ? children
        : row &&
          row.map((cell, index) => {
            if (renderCell) {
              return renderCell(cell, index)
            }
            return <TableCell key={'table-cell-' + index} {...cell} />
          })}
    </tr>
  )
}

export type TableRow = Array<TableCellBaseProps>
export type TableRows = Array<TableRow>

export type TableBodyBaseProps = {
  rows?: TableRows
  renderRow?: (row: TableRow, index: number) => React.ReactNode
}

export type TableBodyProps = TableBodyBaseProps & ComponentProps<'tbody'>

export const TableBody = ({ children, renderRow, rows, ...props }: TableBodyProps) => {
  return (
    <tbody {...props}>
      {children
        ? children
        : rows &&
          rows?.map((row: TableRow, index) => {
            if (renderRow) {
              return renderRow(row, index)
            }
            if (row) {
              return (
                <TableRow key={'table-row-' + index}>
                  {row.map((cell: TableCellBaseProps, index) => {
                    return <TableCell key={'table-cell-' + index} {...cell}></TableCell>
                  })}
                </TableRow>
              )
            }
            return <></>
          })}
    </tbody>
  )
}
